import { Alert, Card, Col, Row, Space, Statistic, Tooltip, Typography, theme } from 'antd'
import {
  ClusterOutlined,
  DollarOutlined,
  DownloadOutlined,
  SwapOutlined,
  TeamOutlined,
  WarningOutlined,
  ThunderboltOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { ActiveSessionsPanel } from '@/features/session/ActiveSessionsPanel'
import { ActiveSessionsTrend } from '@/features/dashboard/ActiveSessionsTrend'
import { AttentionPanel } from '@/features/dashboard/AttentionPanel'
import { AuthHealthPanel } from '@/features/dashboard/AuthHealthPanel'
import { NetworkHealthPanel } from '@/features/dashboard/NetworkHealthPanel'
import { PanelBand } from '@/features/dashboard/PanelBand'
import { TopAgentsPanel } from '@/features/dashboard/TopAgentsPanel'
import { RecentAuditPanel } from '@/features/dashboard/RecentAuditPanel'
import { RecentAuthPanel } from '@/features/dashboard/RecentAuthPanel'
import { TopConsumersPanel } from '@/features/dashboard/TopConsumersPanel'
import { TrafficRevenuePanel } from '@/features/dashboard/TrafficRevenuePanel'
import { SalesCollectionsPanel } from '@/features/dashboard/SalesCollectionsPanel'
import { fetchDashboardReport } from '@/features/reports/reportsApi'
import { fetchStaleSessions, fetchSessionStats } from '@/features/session/sessionApi'
import { fetchSubscribers } from '@/features/subscriber/subscriberApi'
import { useAuth } from '@/shared/auth/useAuth'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatBytes } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { EMPTY_TABLE_STATE } from '@/shared/ui/table/tableState'
import { InconsistencyPanel } from '@/features/session/InconsistencyPanel'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RefreshIndicator } from '@/shared/ui/RefreshIndicator'
import { ScopeHidden } from '@/shared/ui/ScopeHidden'

/**
 * Bosh sahifa yangilanish davri — BITTA joyda.
 *
 * ⚠️ Ilgari `15_000` faqat `stats` so'rovida yozilgan edi va qolgan ikki
 * so'rovda umuman yo'q edi. Raqamni takrorlash o'rniga doimiy: keyingi
 * o'zgartirishda bittasi unutilmasin.
 */
const DASHBOARD_REFRESH_MS = 15_000

/**
 * Bosh sahifa.
 *
 * ⚠️ TRAFIK VA DAROMAD KPI'LARI ENDI BOR — koordinator tuzatishi: `report`
 * modulida `ReportController` yozilgan va ishlayapti (`GET /api/reports/*`,
 * `reportsApi.ts`). Eski izoh ("bu ma'lumot yo'q") ESKIRGAN edi.
 *
 * ⚠️ "BUGUN" NI FRONTEND HISOBLAMAYDI: kun chegarasi tenant vaqt zonasi
 * bo'yicha backendda hisoblanadi (Kobulda 04:30, Toshkentda 05:00). Biz
 * faqat BROWSER LOKAL kunining ISO chegaralarini so'rov sifatida yuboramiz
 * — qaytgan sonni O'ZGARTIRMASDAN ko'rsatamiz, o'zimiz qayta hisoblamaymiz.
 */
export function DashboardPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { can } = usePermissions()

  const canRead = can('subscriber.read')
  const canAudit = can('audit.read')
  const canReport = can('report.read')
  const canFinance = can('finance.dashboard.view')

  // ⚠️ Ranglar QOTIRILMAYDI — mavzu tokenidan.
  //
  // Ilgari bu yerda oltita qotirilgan hex turardi (`#2563eb`, `#16a34a`…).
  // Ular yorug' rejim uchun tanlangan va qorong'ida kontrasti past
  // bo'lardi — mahsulot esa ishlaydigan qorong'i mavzu bilan keladi.
  const { token } = theme.useToken()

  const stats = useQuery({
    queryKey: queryKeys.sessions.stats,
    queryFn: fetchSessionStats,
    enabled: canRead,
    refetchInterval: DASHBOARD_REFRESH_MS,
  })

  /**
   * Osilgan sessiyalar — bosh sahifadagi YAGONA nosozlik ko'rsatkichi.
   *
   * ⚠️ Bu ma'lumot allaqachon bor edi (`GET /api/sessions/reconciliation`),
   * lekin faqat `/sessions?tab=stale` sahifasida ko'rinardi — ya'ni
   * operator muammoni faqat ATAYLAB izlaganda topardi. Bosh sahifada
   * esa "hammasi joyida" degan taassurot qolardi.
   */
  const staleSessions = useQuery({
    queryKey: queryKeys.sessions.stale,
    queryFn: fetchStaleSessions,
    enabled: canRead,
    refetchInterval: DASHBOARD_REFRESH_MS,
  })

  // ⚠️ SANA ORALIG'I YUBORILMAYDI. `GET /api/reports/dashboard` uni umuman
  // qabul qilmaydi: "bugun" TENANT vaqt zonasida serverda hisoblanadi va
  // javobning o'zida `day`/`dayTimezone` bilan tasdiqlanadi. Ilgari bu yerda
  // brauzer kunining ISO chegaralari yuborilardi — server ularni e'tiborsiz
  // qoldirardi, ya'ni parametrlar faqat kesh kalitini shovqinlashtirardi.
  const dashboardReport = useQuery({
    queryKey: queryKeys.reports.dashboard,
    queryFn: fetchDashboardReport,
    enabled: canReport,
    // ⚠️ Bu yerda ilgari `refetchInterval` YO'Q edi va faqat `stats`
    // yangilanardi. Natijada bitta ekranda ikki xil yoshdagi raqam turardi:
    // onlayn abonentlar tirik, trafik va daromad esa sahifa ochilgan
    // paytdagi holatda qotib qolardi. Farq ko'rinmasdi, chunki ikkalasi ham
    // bir xil ko'rinishda chiziladi.
    refetchInterval: DASHBOARD_REFRESH_MS,
  })

  /**
   * Abonentlar soni — ⚠️ IKKI XIL TA'RIF BOR EDI VA IKKALASI BIR EKRANDA.
   *
   * Bu yerda ilgari `GET /api/subscribers?size=1` ning `total` i turardi.
   * U ro'yxatdagi BARCHA yozuvlarni sanaydi, YOPILGANLARNI HAM. Hisobot
   * moduli esa boshqacha sanaydi: `state <> 'CLOSED'`
   * (`ReportSql.COUNT_SUBSCRIBERS`). Sinov muhitida farq 44 va 11 edi —
   * ya'ni "Jami abonentlar: 44" yozuvi 33 ta yopilgan yozuvni tirik
   * mijozdek ko'rsatardi va operator "onlayn 1 / jami 44" ni ko'rib
   * tizimni buzuq deb o'ylardi.
   *
   * Endi manba BITTA: hisobot moduli. U yo'q bo'lsa (huquqsiz) ro'yxat
   * sanogi ishlatiladi, LEKIN BOSHQA YOZUV bilan — "ro'yxatdagi yozuvlar"
   * deb, chunki u aynan shu, "abonentlar soni" emas.
   */
  const recordCount = useQuery({
    queryKey: queryKeys.subscribers.list({ dashboard: true }),
    queryFn: ({ signal }) =>
      fetchSubscribers({ ...EMPTY_TABLE_STATE, pageSize: 1 }, signal).then((page) => page.total),
    // Hisobot bor bo'lsa bu so'rov UMUMAN yuborilmaydi — takroriy so'rov
    // va ikkinchi haqiqat manbai bo'lardi
    enabled: canRead && !canReport,
    refetchInterval: DASHBOARD_REFRESH_MS,
  })

  // Ko'rsatkich uchun: eng ESKI muvaffaqiyatli javob vaqti olinadi, eng
  // yangisi emas. Sabab — sahifada uch so'rov bor va operatorga "bu ekranda
  // ko'rinayotgan eng eski raqam qachonlik" degan savol muhim. Eng yangisini
  // ko'rsatish qolgan ikkitasi qotib qolganda ham "hozirgina" deb yolg'on
  // aytardi.
  // ⚠️ Doira bo'yicha FILTRLANGAN ro'yxat uzunligi — job sanog'i emas.
  //
  // ⚠️ `?? 0` EMAS, `undefined`. Ilgari shu yerda `?? 0` turardi va
  // so'rov yiqilganda karta «Osilgan sessiyalar: 0» deb yozardi — ya'ni
  // eng muhim nosozlik ko'rsatkichi javob kelmaganda «hammasi joyida»
  // bo'lib ko'rinardi. Endi javob bo'lmasa chiziqcha chiqadi.
  const staleVisible = staleSessions.isSuccess ? staleSessions.data.length : undefined

  const updatedCandidates = [
    canRead ? stats.dataUpdatedAt : 0,
    canReport ? dashboardReport.dataUpdatedAt : 0,
    canRead && !canReport ? recordCount.dataUpdatedAt : 0,
    canRead ? staleSessions.dataUpdatedAt : 0,
  ].filter((v) => v > 0)
  const updatedAt = updatedCandidates.length ? Math.min(...updatedCandidates) : 0
  const fetching = stats.isFetching || dashboardReport.isFetching || recordCount.isFetching

  return (
    <>
      <PageHeader
        title={t('nav.dashboard')}
        description={user?.tenant.name}
        extra={
          <RefreshIndicator
            updatedAt={updatedAt}
            fetching={fetching}
            intervalSeconds={DASHBOARD_REFRESH_MS / 1000}
          />
        }
      />

      {!canRead ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('access.deniedTitle')}
          description={t('dashboard.noPermission')}
        />
      ) : null}

      {/*
        ⚠️ TARTIB O'ZGARTIRILDI — "nechta abonent bor" emas, "hozir nima
        buzuq".

        Ilgari birinchi ko'rinadigan raqam jami abonentlar soni edi. U
        kuniga ikki marta o'zgaradi va operator u bilan hech nima
        qilmaydi. Nosozlik ko'rsatkichi esa UMUMAN yo'q edi, holbuki
        ma'lumot allaqachon bor: osilgan sessiyalar.

        Endi birinchi qator — HARAKAT talab qiladigan uchta raqam va
        ularning kontekstidagi abonentlar soni.

        ⚠️ Har karta BOSILADI. Ilgari operator "12 onlayn" ni ko'rib,
        u yerga borish uchun yon menyudan qidirishi kerak edi — ya'ni
        raqam o'lik nuqta edi.

        ⚠️ KARTALAR BIR XIL BALANDLIKDA (`height: 100%`). Ilgari faqat
        "Faol sessiyalar" kartasi grafik tufayli baland edi va qatorda
        pog'ona hosil bo'lardi.
      */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {/*
          ⚠️ IKKI KARTA BITTA BO'LDI (tester-2, BUG-004).

          Ilgari yonma-yon ikkita karta turardi — «Onlayn abonentlar» va
          «Faol sessiyalar» — va IKKALASI HAM `/sessions` ga olib borardi
          (o'lchandi: `href` ikkalasida `"/sessions"`). Tashqi tekshiruvchi
          buni "bittasi keraksiz" deb o'qidi va u haq edi: bir ekranda,
          bir manzilga, bir mavzuda ikki katak.

          ⚠️ LEKIN IKKINCHI SON YO'QOLMADI. Ular BOSHQA-BOSHQA narsa:
          ko'p qurilmali abonent sessiyalar sonida bir necha marta,
          abonentlar sonida BIR marta sanaladi (`SessionsPage` izohi).
          Shuning uchun sessiyalar soni asosiy sonning ostidagi izoh
          qatoriga tushdi — `dashboard.onlineSessionsHint` kaliti buning
          uchun ALLAQACHON yozilgan edi va hech qayerda ishlatilmasdi.

          ⚠️ Trend chizig'i shu kartada qoladi: u aynan FAOL SESSIYALAR
          soni bo'yicha yig'iladi (`ActiveSessionsTrend`).
        */}
        <Col xs={24} sm={12} lg={8}>
          <Link to="/sessions">
            <Card hoverable loading={stats.isPending} style={{ height: '100%' }}>
              <Statistic
                title={t('dashboard.onlineSubscribers')}
                value={stats.data?.onlineSubscribers ?? '—'}
                prefix={<ClusterOutlined style={{ color: token.colorSuccess }} />}
              />
              <Space size={6} style={{ marginTop: 4 }}>
                <ThunderboltOutlined style={{ color: token.colorWarning }} />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {stats.data
                    ? t('dashboard.onlineSessionsHint', { count: stats.data.activeSessions })
                    : t('common.dash')}
                </Typography.Text>
              </Space>
              {canRead ? (
                <div style={{ marginTop: 8 }}>
                  <ActiveSessionsTrend value={stats.data?.activeSessions} />
                </div>
              ) : null}
            </Card>
          </Link>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          {/*
            ⚠️ ENG MUHIM KARTA — u faqat MUAMMO bo'lganda qizil bo'ladi.
            Osilgan sessiya = abonent panelda "onlayn" ko'rinadi, aslida
            esa yo'q. Unga yuborilgan har buyruq 5 soniya kutib NAK
            qaytaradi. Ilgari bu son faqat `/sessions?tab=stale` da,
            ya'ni hech kim ko'rmaydigan joyda edi.
          */}
          <Link to="/sessions?tab=stale">
            <Card hoverable loading={staleSessions.isPending} style={{ height: '100%' }}>
              {/*
                ⚠️ `staleSessions` EMAS, `stale.length`.

                `staleSessions` — rekonsiliatsiya jobining sanog'i va u
                TIZIM bo'yicha: `radacct` da tenant ustuni yo'q, job
                esa doirasiz o'qiydi. Ya'ni u BOSHQA tenantlarning
                sessiyalarini ham sanaydi.

                `stale` ro'yxati esa resource'da foydalanuvchi doirasi
                bo'yicha filtrlanadi. Ro'yxat uzunligini olish — bu
                yerdagi yagona to'g'ri son.

                Aks holda operator "5" ni ko'rib, bosib, bo'sh ro'yxat
                topardi va raqamga ishonishni to'xtatardi.
              */}
              <Statistic
                title={t('reconcile.staleTitle')}
                value={staleVisible ?? '—'}
                prefix={
                  <WarningOutlined
                    style={{
                      color: staleVisible
                        ? token.colorError
                        : token.colorTextQuaternary,
                    }}
                  />
                }
                valueStyle={staleVisible ? { color: token.colorError } : undefined}
              />
            </Card>
          </Link>
        </Col>

        {/*
          ⚠️ ABONENTLAR SONI SHU QATORDA, "bugungi" raqamlar bilan emas.

          Sabab: u onlayn/faol/osilgan uchligining MAXRAJI — "1 ta onlayn"
          11 ta abonentda va 1100 ta abonentda butunlay boshqa ma'no
          beradi. Trafik va daromad esa boshqa savol ("bugun qancha").
        */}
        <Col xs={24} sm={12} lg={8}>
          <Link to="/subscribers">
            <Card hoverable loading={canReport ? dashboardReport.isPending : recordCount.isPending} style={{ height: '100%' }}>
              <Statistic
                title={canReport ? t('dashboard.totalSubscribers') : t('dashboard.recordCount')}
                value={
                  canReport
                    ? (dashboardReport.data?.totalSubscribers ?? undefined)
                    : (recordCount.data ?? undefined)
                }
                formatter={(v) =>
                  v === undefined ? <ScopeHidden hint={t('dashboard.scopeHiddenHint')} /> : String(v)
                }
                prefix={<TeamOutlined style={{ color: token.colorTextSecondary }} />}
              />
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {canReport ? t('dashboard.totalSubscribersHint') : t('dashboard.recordCountHint')}
              </Typography.Text>
            </Card>
          </Link>
        </Col>
      </Row>

      {/*
        Ikkinchi qator — "BUGUN" ning uchta raqami.

        ⚠️ RANGLAR ENDI TOKENDAN. Bu yerda uchta qotirilgan hex turardi
        (`#0891b2`, `#7c3aed`, `#16a34a`) — holbuki shu faylning o'z izohi
        "ranglar qotirilmaydi" deb yozilgan edi. Ular yorug' rejim uchun
        tanlangan va qorong'ida kontrastini yo'qotardi.

        ⚠️ HAR KARTA ENDI BOSILADI. Ilgari trafik va daromad kartalari
        o'lik nuqta edi: operator raqamni ko'rib, tafsilotga o'tish uchun
        yon menyudan Hisobotlarni qidirishi kerak edi.

        ⚠️ USTUN ENI YUQORIDAGI QATOR BILAN BIR XIL BO'LISHI SHART.
        O'lchov (1920px) ilgari ko'rsatgan edi: to'rt ustunli qator
        chegaralari 404/808/1212, uch ustunlisiniki 539/1077 — birorta
        tik chiziq mos kelmasdi va ko'z ikkita boshqa-boshqa panjarani
        ko'rardi. Ikkala qator ham HOZIR uchtalik (8+8+8), chunki tepada
        ikki sessiya kartasi bittaga birlashdi (BUG-004).

        ⚠️ «BUGUN QAYSI KUN» IZOHI ENDI QATORDAN TASHQARIDA VA QISQA —
        sabab pastdagi izohda (BUG-001).
      */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {canReport ? (
          <>
            <Col xs={24} sm={12} lg={8}>
              <Link to="/reports">
                <Card hoverable size="small" loading={dashboardReport.isPending} style={{ height: '100%' }}>
                  <Statistic
                    title={t('dashboard.todayUpload')}
                    value={
                      dashboardReport.isError
                        ? '—'
                        : dashboardReport.data && dashboardReport.data.uploadBytes === null
                          ? ''
                          : formatBytes(dashboardReport.data?.uploadBytes)
                    }
                    formatter={(v) =>
                      v === '' ? <ScopeHidden hint={t('dashboard.scopeHiddenHint')} /> : String(v)
                    }
                    prefix={<UploadOutlined style={{ color: token.colorSuccess }} />}
                  />
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {t('dashboard.uploadHint')}
                  </Typography.Text>
                </Card>
              </Link>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Link to="/reports">
                <Card hoverable size="small" loading={dashboardReport.isPending} style={{ height: '100%' }}>
                  <Statistic
                    title={t('dashboard.todayDownload')}
                    value={
                      dashboardReport.isError
                        ? '—'
                        : dashboardReport.data && dashboardReport.data.downloadBytes === null
                          ? ''
                          : formatBytes(dashboardReport.data?.downloadBytes)
                    }
                    formatter={(v) =>
                      v === '' ? <ScopeHidden hint={t('dashboard.scopeHiddenHint')} /> : String(v)
                    }
                    prefix={<DownloadOutlined style={{ color: token.colorPrimary }} />}
                  />
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {t('dashboard.downloadHint')}
                  </Typography.Text>
                </Card>
              </Link>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Link to="/reports">
                <Card hoverable size="small" loading={dashboardReport.isPending} style={{ height: '100%' }}>
                  {canFinance ? (
                    <>
                      <Statistic
                        title={t('dashboard.todayTotal')}
                        value={
                          dashboardReport.isError
                            ? '—'
                            : dashboardReport.data && dashboardReport.data.totalBytes === null
                              ? ''
                              : formatBytes(dashboardReport.data?.totalBytes)
                        }
                        formatter={(v) =>
                          v === '' ? <ScopeHidden hint={t('dashboard.scopeHiddenHint')} /> : String(v)
                        }
                        prefix={<SwapOutlined style={{ color: token.colorPrimary }} />}
                      />
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        {t('dashboard.totalHint')}
                      </Typography.Text>
                    </>
                  ) : (
                    <>
                      <Statistic
                        title={t('dashboard.todayRevenue')}
                        value={
                          dashboardReport.isError
                            ? '—'
                            : dashboardReport.data && dashboardReport.data.revenue === null
                              ? ''
                              : formatMoneyValue(dashboardReport.data?.revenue)
                        }
                        formatter={(v) =>
                          v === '' ? <ScopeHidden hint={t('dashboard.revenueHiddenHint')} /> : String(v)
                        }
                        prefix={<DollarOutlined style={{ color: token.colorSuccess }} />}
                      />
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        {t('dashboard.revenueHint')}
                      </Typography.Text>
                    </>
                  )}
                </Card>
              </Link>
            </Col>
          </>
        ) : null}
      </Row>

      {/*
        ⚠️ KPI MAYDONIDAN UZUN JUMLA OLIB TASHLANDI (tester-2, BUG-001).

        Bu yerda 129 belgilik butun bir jumla turardi va u KARTALAR
        QATORINING ichida, raqamlarning yonida chizilardi (o'lchandi:
        «Bugun» ko'rsatkichlari Asia/Kabul zonasidagi 2026-08-16 kuniga
        tegishli — bu tashkilotingizning kun chegarasi, brauzerniki
        emas.). Tashqi tekshiruvchi buni "dashboardda ortiqcha text" deb
        yozdi va u haq: KPI qatori qisqa yorliq va raqamdan iborat
        bo'lishi kerak.

        ⚠️ MA'LUMOT YO'QOLMADI, JOYI VA SHAKLI O'ZGARDI. Kun chegarasi
        TENANT zonasida hisoblanadi (Kobulda 04:30, Toshkentda 05:00) va
        brauzerdagi "bugun" bilan mos kelmasligi mumkin — buni operator
        BILISHI shart, aks holda hisobotdagi va ro'yxatdagi son farq
        qilganda sababini topolmaydi. Endi qatordan KEYIN bitta qisqa
        yorliq turadi («Bugun: 2026-08-16 · Asia/Kabul»), to'liq
        tushuntirish esa uning TOOLTIPIDA — ya'ni bir bosishda, lekin
        ekranni egallamasdan.

        ⚠️ Ma'lumot kelmaguncha hech narsa chizilmaydi: "Bugun: —"
        yozuvi hech kimga kerak emas.
      */}
      {canReport && dashboardReport.data ? (
        <div style={{ marginTop: -8, marginBottom: 16 }}>
          <Tooltip
            title={t('dashboard.dayNote', {
              day: dashboardReport.data.day,
              zone: dashboardReport.data.dayTimezone,
            })}
          >
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('dashboard.dayLabel', {
                day: dashboardReport.data.day,
                zone: dashboardReport.data.dayTimezone,
              })}
            </Typography.Text>
          </Tooltip>
        </div>
      ) : null}

      {/*
        ⚠️ XATO BANNERI ENDI SHU YERDA — o'zi tushuntirayotgan raqamlar
        OSTIDA. Ilgari u sahifaning eng pastida, faol sessiyalar
        jadvalidan keyin turardi: yuqoridagi uchta karta chiziqcha
        ko'rsatib turar, sababi esa bir ekran pastda yozilgan bo'lardi va
        operator ularni bog'lay olmasdi.
      */}
      {canReport && dashboardReport.isError ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('dashboard.reportErrorTitle')}
          description={translateError(dashboardReport.error, t)}
        />
      ) : null}

      {canFinance ? <SalesCollectionsPanel refreshMs={DASHBOARD_REFRESH_MS} /> : null}

      {/*
        ⚠️ "E'TIBOR TALAB QILADI" QATORI — grafiklardan YUQORIDA.

        Grafik o'tmish haqida, bu qator esa BUGUN qilinadigan ish haqida:
        kimga bog'lanish kerak (paketi tugayapti), qaysi qurilma jim
        (tarmoq), ulanishlar rad etilyaptimi. Operator ekranni ochganda
        birinchi shularni ko'rishi kerak — grafikni esa u ATAYLAB
        qidirganda.
      */}
      {/*
        ⚠️ `Row`/`Col` EMAS, `PanelBand`. To'liq sabab va o'lchovlar
        `PanelBand.tsx` javadoc'ida. Qisqasi ikkita: `xl={6}` beshta
        panelga 30 ustun talab qilardi (qator 24 ta) va beshinchi panel
        yonida har kenglikda bo'sh joy qolardi; `null` qaytargan panelning
        ustuni esa baribir joy egallardi.
      */}
      <div style={{ marginBottom: 16 }}>
        <PanelBand>
          <AttentionPanel />
          <NetworkHealthPanel />
          <AuthHealthPanel />
          <TopAgentsPanel />
          {/*
            ⚠️ Nomuvofiqlik paneli AYNAN shu qatorda: u ham "bugun
            qilinadigan ish" toifasidan. Boshqa joyga qo'yish uni
            grafiklar orasida ko'mib yuborardi va aynan shu ma'lumot
            hech qayerda ko'rinmagani uchun qo'shilgan edi.
          */}
          <InconsistencyPanel />
        </PanelBand>
      </div>

      {/* ⚠️ Ikkala panel ham `report.read` talab qiladi va huquq yo'q
          bo'lsa IKKALASI `null` qaytaradi — qator umuman chizilmasin,
          aks holda faqat pastki chekinish qolib ketardi */}
      {canReport ? (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={16}>
            <TrafficRevenuePanel />
          </Col>
          <Col xs={24} lg={8}>
            <TopConsumersPanel />
          </Col>
        </Row>
      ) : null}

      {/*
        ⚠️ FAOL SESSIYALAR JADVALI ENDI BUTUN ENDA, yonida ro'yxat yo'q.

        Ikki o'lchangan sabab:

        1. BO'SH JOY. Ilgari jadval `lg={16}` da, o'ng ustunda esa ikkita
           beshqatorli ro'yxat ustma-ust turardi. Ularning balandligi
           bog'liq emas: sessiya yo'q kunda (kechasi, kichik provayderda)
           jadval 280px, o'ng ustun esa 820px bo'lardi — ya'ni chapda
           1080×540px oq bo'shliq qolardi. O'lchov 1920px da aynan shunday
           chiqdi.

        2. JADVALGA ENI YETMASDI. `ActiveSessionsPanel` da
           `scroll={{ x: 1200 }}` — jadval 1200px talab qiladi. `lg={16}`
           esa 1920px da 1077px, 1440px da 779px berardi, ya'ni ustunlarning
           bir qismi doim gorizontal scroll ortida qolardi. Butun enda
           1920px da 1616px chiqadi — scroll umuman kerak bo'lmaydi.

        ⚠️ Jadval pastdagi ikki ro'yxatdan YUQORIDA qoladi: u operativ
        ma'lumot, ro'yxatlar esa tarix. Jadval uzun bo'lsa (25 qator)
        ro'yxatlar pastga suriladi — bu to'g'ri tartib, chunki uzun va
        o'zgaruvchan balandlikdagi mazmun qisqalarini itarib turmasligi
        kerak edi.
      */}
      {canRead ? (
        <div style={{ marginBottom: 16 }}>
          <ActiveSessionsPanel />
        </div>
      ) : null}

      {/*
        ⚠️ IKKI JURNAL — YONMA-YON, TENG YARIMDA.

        Ilgari ikkalasi bitta tor ustunda (lg={8}) ustma-ust turardi.
        O'lchov 1440px da 389px berardi va shu enda sarlavhaning o'zi
        qirqilardi («Oxirgi autentifikatsiya urinishl…»), qatorlarda esa
        «Foydalanuvchi cb655d0d…» ko'rinardi. Endi har biriga 584px
        tegadi — ya'ni +50% va qirqilish yo'qoladi.

        ⚠️ `RecentAuditPanel` dagi «8 emas, 5» cheklovi aynan shu eski
        joylashuv tufayli qo'yilgan edi: ikkita beshqatorli ro'yxat chap
        ustundan ikki barobar uzayib ketardi. Endi ular yonma-yon va bu
        cheklov faqat "bosh sahifa qisqa ko'rinish" ma'nosida qoladi.

        ⚠️ Bittasi huquqsiz bo'lsa ikkinchisi BUTUN ENNI oladi — bo'sh
        yarim qolmaydi. AGENT rolida aynan shunday: `audit.read` yo'q.

        ⚠️ CHEGARA `lg` EMAS, `xl` (1200px). 1024px da yarim ustun 376px
        beradi va o'lchov shu enda amal nomini o'rtasidan sindirganini
        ko'rsatdi: «TOTP_VERIF / Y», «LEDGER_RECON / CILE». 1200px dan
        past ekranda ikkalasi butun enda, ustma-ust turadi — bu yerda
        bo'shliq baribir hosil bo'lmaydi.

        Ular ATAYLAB alohida kartochkada: audit — XODIM nima qilgani,
        ikkinchisi esa ABONENT ulanishga urinib nima javob olgani.
        Ikkalasi bitta ro'yxatga qo'shilsa, "kim qildi" degan ustun
        ma'nosini yo'qotardi.
      */}
      {canAudit || canRead ? (
        <Row gutter={[16, 16]}>
          {canAudit ? (
            <Col xs={24} xl={canRead ? 12 : 24}>
              <Card
                title={t('audit.subscriberTitle')}
                extra={<Link to="/audit">{t('audit.openFull')}</Link>}
              >
                <RecentAuditPanel />
              </Card>
            </Col>
          ) : null}

          {canRead ? (
            <Col xs={24} xl={canAudit ? 12 : 24}>
              <Card
                title={t('dashboard.recentAuth')}
                extra={<Link to="/sessions?tab=auth">{t('audit.openFull')}</Link>}
              >
                <RecentAuthPanel />
              </Card>
            </Col>
          ) : null}
        </Row>
      ) : null}
    </>
  )
}
